// Smart Daara - Course Viewer Page
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { courseAPI, progressAPI, quizAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { buildTenantPath } from '../../lib/routing';
import { YouTubePlayer } from '../../components/shared/YouTubePlayer';
import BunnyVideoPlayer from '../../components/shared/BunnyVideoPlayer';
import PDFViewer from '../../components/shared/PDFViewer';
import { 
  Play, CheckCircle, Lock, ChevronLeft, ChevronRight, 
  BookOpen, FileText, Clock, ArrowLeft, FileQuestion, AlertTriangle, Video
} from 'lucide-react';
import { toast } from 'sonner';

export default function CourseViewer() {
  const { courseId, tenantSlug } = useParams();
  const { user } = useAuth();
  const { t, language, getText } = useLanguage();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [quizAttempts, setQuizAttempts] = useState({});
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [lessonProgress, setLessonProgress] = useState({});
  const [courseProgress, setCourseProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showEndOfLessonModal, setShowEndOfLessonModal] = useState(false);
  const shownModalRef = useRef(new Set());
  
  const videoRef = useRef(null);
  const progressInterval = useRef(null);

  useEffect(() => {
    loadCourseData();
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [courseId]);

  const loadCourseData = async (autoSelectLesson = true) => {
    try {
      // Load course
      const courseResponse = await courseAPI.getCourse(courseId);
      setCourse(courseResponse.data);

      // Enroll in course
      await progressAPI.enrollInCourse(courseId);

      // Load lessons with progress
      const lessonsProgressResponse = await progressAPI.getCourseLessonsProgress(courseId);
      const lessonsData = lessonsProgressResponse.data || [];
      
      // Load quizzes for the course
      let quizzesData = [];
      let quizAttemptsMap = {};
      try {
        const quizzesResponse = await quizAPI.listQuizzes(courseId);
        quizzesData = quizzesResponse.data || [];
        setQuizzes(quizzesData);
        
        // Load attempts for each quiz to check if passed
        for (const quiz of quizzesData) {
          try {
            const attemptsResponse = await quizAPI.getMyAttempts(quiz.id);
            const attempts = attemptsResponse.data || [];
            const hasPassed = attempts.some(a => a.is_passed);
            quizAttemptsMap[quiz.id] = { 
              attempts, 
              hasPassed,
              bestScore: attempts.length > 0 ? Math.max(...attempts.map(a => a.score_percentage || 0)) : 0
            };
          } catch (e) {
            quizAttemptsMap[quiz.id] = { attempts: [], hasPassed: false, bestScore: 0 };
          }
        }
        setQuizAttempts(quizAttemptsMap);
      } catch (e) {
        console.error('Error loading quizzes:', e);
      }

      // Build combined list of lessons and quizzes sorted by order
      // And determine lock status based on quiz completion
      const combinedItems = [];
      
      // Add lessons
      lessonsData.forEach(lesson => {
        combinedItems.push({
          ...lesson,
          type: 'lesson',
          order: lesson.order || 0
        });
      });
      
      // Add quizzes
      quizzesData.forEach(quiz => {
        combinedItems.push({
          ...quiz,
          lesson_id: quiz.id,
          type: 'quiz',
          order: quiz.order || 0
        });
      });
      
      // Sort by order
      combinedItems.sort((a, b) => a.order - b.order);
      
      // Determine lock status based on preceding quizzes
      const processedItems = [];
      let isBlocked = false;
      let blockingQuizId = null;
      
      for (let i = 0; i < combinedItems.length; i++) {
        const item = combinedItems[i];
        const itemCopy = { ...item };
        
        if (item.type === 'quiz') {
          const quizPassed = quizAttemptsMap[item.id]?.hasPassed || false;
          itemCopy.is_locked = isBlocked;
          itemCopy.is_completed = quizPassed;
          
          // If this quiz is not passed, block subsequent items
          if (!quizPassed && !isBlocked) {
            isBlocked = true;
            blockingQuizId = item.id;
          }
        } else {
          // It's a lesson
          itemCopy.is_locked = isBlocked || item.is_locked;
          if (isBlocked && !item.is_locked) {
            itemCopy.blocked_by_quiz = blockingQuizId;
          }
        }
        
        processedItems.push(itemCopy);
      }
      
      setLessons(processedItems);

      // Build progress map
      const progressMap = {};
      processedItems.forEach(l => {
        if (l.type === 'lesson') {
          progressMap[l.lesson_id] = {
            progress_percentage: l.progress_percentage || 0,
            is_completed: l.is_completed || false,
            is_locked: l.is_locked || false
          };
        }
      });
      setLessonProgress(progressMap);

      // Calculate course progress
      const lessonItems = processedItems.filter(i => i.type === 'lesson');
      const completed = lessonItems.filter(l => l.is_completed).length;
      const total = lessonItems.length;
      setCourseProgress(total > 0 ? (completed / total) * 100 : 0);

      // Set first unlocked lesson as current (skip when just refreshing the sidebar)
      if (autoSelectLesson) {
        const firstUnlocked = processedItems.find(l => !l.is_locked && !l.is_completed && l.type === 'lesson') || 
                             processedItems.find(l => l.type === 'lesson');
        if (firstUnlocked && firstUnlocked.type === 'lesson') {
          await loadLesson(firstUnlocked.lesson_id);
        }
      }
    } catch (error) {
      console.error('Error loading course:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const loadLesson = async (lessonId) => {
    try {
      const lessonResponse = await courseAPI.getLesson(lessonId);
      setCurrentLesson(lessonResponse.data);

      // Start tracking progress
      await progressAPI.startLesson(lessonId);
    } catch (error) {
      console.error('Error loading lesson:', error);
    }
  };

  const handleLessonSelect = async (lesson) => {
    if (lesson.is_locked) {
      if (lesson.blocked_by_quiz) {
        const blockingQuiz = quizzes.find(q => q.id === lesson.blocked_by_quiz);
        const quizTitle = blockingQuiz?.title_fr || blockingQuiz?.title || 'le devoir';
        toast.error(language === 'fr' 
          ? `Vous devez d'abord réussir ${quizTitle} pour accéder à cette leçon`
          : `You must first pass ${quizTitle} to access this lesson`);
      } else {
        toast.error(language === 'fr' 
          ? 'Terminez la leçon précédente d\'abord'
          : 'Complete the previous lesson first');
      }
      return;
    }
    
    if (lesson.type === 'quiz') {
      navigate(`/student/course/${courseId}/quiz/${lesson.id}`);
      return;
    }
    
    await loadLesson(lesson.lesson_id);
  };

  const updateVideoProgress = async (watchedSeconds, totalDuration) => {
    if (!currentLesson) return;
    
    try {
      const progressPercent = totalDuration > 0 ? (watchedSeconds / totalDuration) * 100 : 0;
      
      await progressAPI.updateLessonProgress(currentLesson.id, {
        video_watched_seconds: Math.floor(watchedSeconds),
        video_last_position: Math.floor(watchedSeconds),
        progress_percentage: Math.min(progressPercent, 100)
      });

      // Check if completed (95% threshold)
      if (progressPercent >= 95) {
        setLessonProgress(prev => ({
          ...prev,
          [currentLesson.id]: { ...prev[currentLesson.id], is_completed: true, progress_percentage: 100 }
        }));
        toast.success(language === 'fr' ? 'Leçon terminée!' : 'Lesson completed!');
        loadCourseData(); // Reload to unlock next lesson
      }
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleProgressUpdate = async (percent) => {
    if (!currentLesson) return;
    
    try {
      // Bunny videos and PDFs save their own exact progress (seconds/pages).
      // Re-saving here with a fake duration corrupted video_watched_seconds — never write it again.
      const savesItself = !!currentLesson.bunny_video_url || currentLesson.lesson_type === 'pdf';
      if (!savesItself) {
        await progressAPI.updateLessonProgress(currentLesson.id, {
          progress_percentage: percent
        });
      }

      // Update local state
      const alreadyCompleted = lessonProgress[currentLesson.id]?.is_completed;
      setLessonProgress(prev => ({
        ...prev,
        [currentLesson.id]: { 
          ...prev[currentLesson.id], 
          progress_percentage: percent,
          is_completed: percent >= 95
        }
      }));

      // Lesson completed (video >= 95%, PDF = 100%) → end-of-lesson modal (once per lesson)
      if (percent >= 95 && !alreadyCompleted && !shownModalRef.current.has(currentLesson.id)) {
        shownModalRef.current.add(currentLesson.id);
        loadCourseData(false); // Refresh sidebar without switching lesson
        setShowEndOfLessonModal(true);
      } else if (percent === 50) {
        toast.info(language === 'fr' ? 'À mi-chemin! Continuez!' : 'Halfway there! Keep going!');
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error(language === 'fr' ? 'Erreur de mise à jour' : 'Update error');
    }
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
    return match ? match[1] : null;
  };

  const goToNextLesson = () => {
    const currentIndex = lessons.findIndex(l => l.lesson_id === currentLesson?.id);
    if (currentIndex >= 0 && currentIndex < lessons.length - 1) {
      const nextLesson = lessons[currentIndex + 1];
      if (!nextLesson.is_locked) {
        loadLesson(nextLesson.lesson_id);
      }
    }
  };

  const goToPreviousLesson = () => {
    const currentIndex = lessons.findIndex(l => l.lesson_id === currentLesson?.id);
    if (currentIndex > 0) {
      loadLesson(lessons[currentIndex - 1].lesson_id);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="course-viewer">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-outfit font-bold text-slate-900 truncate">
            {getText(course, 'title')}
          </h1>
          <div className="flex items-center gap-4 mt-1">
            <span className="text-sm text-slate-500">
              {lessons.length} {t('lessons')}
            </span>
            <div className="flex items-center gap-2 flex-1 max-w-xs">
              <Progress value={courseProgress} className="h-2" />
              <span className="text-sm font-medium text-emerald-700">
                {Math.round(courseProgress)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lesson Content */}
        <div className="lg:col-span-2 min-w-0 space-y-4">
          {currentLesson ? (
            <Card className="border-0 shadow-sm overflow-hidden">
              {/* Bunny Video Player */}
              {currentLesson.lesson_type === 'video' && currentLesson.bunny_video_url && (
                <BunnyVideoPlayer
                  videoUrl={currentLesson.bunny_video_url}
                  lessonId={currentLesson.id}
                  restrictSeek={user?.role === 'student'}
                  onProgressUpdate={(progress) => handleProgressUpdate(progress)}
                />
              )}
              
              {/* YouTube Video Player with Automatic Tracking */}
              {currentLesson.lesson_type === 'video' && currentLesson.youtube_url && !currentLesson.bunny_video_url && (
                <YouTubePlayer
                  videoId={extractYouTubeId(currentLesson.youtube_url)}
                  initialProgress={lessonProgress[currentLesson.id]?.progress_percentage || 0}
                  language={language}
                  isStudent={user?.role === 'student'}
                  onProgressUpdate={(progress, currentTime, duration) => {
                    handleProgressUpdate(progress);
                  }}
                />
              )}

              {/* PDF Viewer with Progress Tracking */}
              {currentLesson.lesson_type === 'pdf' && (
                <PDFViewer
                  lessonId={currentLesson.id}
                  pdfUrl={currentLesson.pdf_url}
                  totalPages={currentLesson.pdf_pages || 1}
                  onComplete={() => {
                    handleProgressUpdate(100);
                  }}
                />
              )}

              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">
                  {getText(currentLesson, 'title')}
                </h2>
                <p className="text-slate-600">
                  {getText(currentLesson, 'description') || (language === 'fr' ? 'Aucune description' : 'No description')}
                </p>

                {/* Navigation */}
                <div className="flex flex-wrap justify-between items-center gap-2 mt-6 pt-4 border-t border-slate-200">
                  <Button
                    variant="outline"
                    onClick={goToPreviousLesson}
                    disabled={lessons.findIndex(l => l.lesson_id === currentLesson?.id) === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    {t('previousLesson')}
                  </Button>
                  
                  <Button
                    onClick={() => updateVideoProgress(currentLesson.video_duration_seconds * 0.95, currentLesson.video_duration_seconds)}
                    className={`${user?.role === 'student' ? 'bg-gray-400 cursor-not-allowed opacity-50' : 'bg-emerald-700 hover:bg-emerald-800'}`}
                    disabled={user?.role === 'student'}
                    title={user?.role === 'student' ? (language === 'fr' ? 'Réservé aux enseignants' : 'Teachers only') : ''}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t('markComplete')}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={goToNextLesson}
                    disabled={
                      lessons.findIndex(l => l.lesson_id === currentLesson?.id) === lessons.length - 1 ||
                      lessons[lessons.findIndex(l => l.lesson_id === currentLesson?.id) + 1]?.is_locked
                    }
                  >
                    {t('nextLesson')}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-8 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">
                  {language === 'fr' ? 'Sélectionnez une leçon' : 'Select a lesson'}
                </p>
              </CardContent>
            </Card>
          )}

          {/* End-of-lesson modal */}
          <Dialog open={showEndOfLessonModal} onOpenChange={setShowEndOfLessonModal}>
            <DialogContent className="sm:max-w-md" data-testid="end-of-lesson-modal">
              {(() => {
                const currentIndex = lessons.findIndex(l => l.lesson_id === currentLesson?.id);
                const isLastLesson = currentIndex >= 0 && currentIndex === lessons.length - 1;
                return (
                  <>
                    <DialogHeader>
                      <DialogTitle className="text-center text-xl">
                        {isLastLesson
                          ? (language === 'fr' ? '🎓 Cours terminé, félicitations !' : '🎓 Course completed, congratulations!')
                          : (language === 'fr' ? '🎉 Leçon terminée !' : '🎉 Lesson completed!')}
                      </DialogTitle>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-center">
                      <Button
                        variant="outline"
                        onClick={() => setShowEndOfLessonModal(false)}
                        data-testid="review-lesson-btn"
                      >
                        {language === 'fr' ? 'Revoir le cours' : 'Review the lesson'}
                      </Button>
                      {isLastLesson ? (
                        <Button
                          className="bg-emerald-700 hover:bg-emerald-800"
                          onClick={() => {
                            setShowEndOfLessonModal(false);
                            navigate(buildTenantPath(tenantSlug, 'student'));
                          }}
                          data-testid="back-to-dashboard-btn"
                        >
                          {language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
                        </Button>
                      ) : (
                        <Button
                          className="bg-emerald-700 hover:bg-emerald-800"
                          onClick={() => {
                            goToNextLesson();
                            setShowEndOfLessonModal(false);
                          }}
                          data-testid="next-lesson-modal-btn"
                        >
                          {language === 'fr' ? 'Passer au suivant' : 'Go to next lesson'}
                        </Button>
                      )}
                    </DialogFooter>
                  </>
                );
              })()}
            </DialogContent>
          </Dialog>
        </div>

        {/* Lesson List */}
        <Card className="border-0 shadow-sm h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-outfit">
              {t('lessons')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="space-y-1 p-3">
                {lessons.map((item, index) => {
                  const isQuiz = item.type === 'quiz';
                  const progress = isQuiz ? null : lessonProgress[item.lesson_id] || {};
                  const isActive = !isQuiz && currentLesson?.id === item.lesson_id;
                  const quizInfo = isQuiz ? quizAttempts[item.id] : null;
                  
                  return (
                    <button
                      key={item.lesson_id || item.id}
                      onClick={() => handleLessonSelect(item)}
                      disabled={item.is_locked}
                      className={`w-full text-left p-3 rounded-lg transition-all ${
                        isActive 
                          ? 'bg-emerald-50 border border-emerald-200' 
                          : item.is_locked
                          ? 'opacity-50 cursor-not-allowed'
                          : isQuiz
                          ? 'hover:bg-amber-50'
                          : 'hover:bg-slate-50'
                      }`}
                      data-testid={`lesson-item-${index}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          item.is_completed 
                            ? 'bg-emerald-100 text-emerald-700'
                            : item.is_locked
                            ? 'bg-slate-100 text-slate-400'
                            : isQuiz
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {item.is_completed ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : item.is_locked ? (
                            <Lock className="w-4 h-4" />
                          ) : isQuiz ? (
                            <FileQuestion className="w-4 h-4" />
                          ) : item.lesson_type === 'video' ? (
                            item.bunny_video_url ? <Video className="w-4 h-4" /> : <Play className="w-4 h-4" />
                          ) : (
                            <FileText className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm truncate ${
                            isActive ? 'text-emerald-700' : 
                            isQuiz ? 'text-amber-800' : 
                            'text-slate-700'
                          }`}>
                            {index + 1}. {item.title_fr || item.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs capitalize ${
                              isQuiz ? 'text-amber-600 font-medium' : 'text-slate-500'
                            }`}>
                              {isQuiz 
                                ? (language === 'fr' ? 'Devoir' : 'Quiz')
                                : item.lesson_type
                              }
                            </span>
                            {isQuiz && quizInfo && quizInfo.bestScore > 0 && !quizInfo.hasPassed && (
                              <span className="text-xs text-red-600">
                                {language === 'fr' ? 'Meilleur:' : 'Best:'} {Math.round(quizInfo.bestScore)}%
                              </span>
                            )}
                            {!isQuiz && !item.is_locked && !item.is_completed && progress.progress_percentage > 0 && (
                              <span className="text-xs text-amber-600">
                                {Math.round(progress.progress_percentage)}%
                              </span>
                            )}
                          </div>
                          {item.blocked_by_quiz && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                              <AlertTriangle className="w-3 h-3" />
                              {language === 'fr' ? 'Réussir le devoir précédent' : 'Pass previous quiz'}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
